import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  BookOpenText,
  CalendarBlank,
  CaretDown,
  CheckCircle,
  Circle,
  EnvelopeSimple,
  FileText,
  Lightbulb,
  Lightning,
  PlugsConnected,
  Rocket,
  ShieldCheck,
  Sparkle,
  Browsers,
  TreeStructure,
  UsersThree,
  X,
  type Icon as PhosphorIconType,
} from '@phosphor-icons/react'
import {
  SUPPORT_EMAIL,
  SUPPORT_MAILTO,
  WARMUP_TIP,
  isLaunchHero,
  remainingSteps,
  stepsForTier,
  tierSummary,
  type OnboardingStatus,
  type OnboardingStep,
  type StepTier,
} from './onboardingLogic'

// These panels render inside DashboardPage, which scopes the --ed-* palette
// (dark/light) on its root, so they pick up the theme with no extra wiring.
//
// Type and metrics follow design-aesthetics.md: Inter at the 510 signature
// weight for app surfaces (Instrument Serif is reserved for the page H1, §2/§23),
// Berkeley Mono caps for eyebrows in the neutral tier (§2), h-9 / rounded-md
// controls (§53), rounded-lg p-5 cards with gap-4 (§5), 15px/510 card titles
// and 13px/1.5 descriptions (§2).

const easeOutExpo = [0.16, 1, 0.3, 1] as const

const monoFont: React.CSSProperties = {
  fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  fontWeight: 400,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
}

const cardTitle: React.CSSProperties = { fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }
const cardDesc: React.CSSProperties = { fontSize: '13px', lineHeight: 1.5, letterSpacing: '-0.13px' }
// Same numeral treatment as the dashboard KpiCard.
const bigNumber: React.CSSProperties = { fontSize: '30px', fontWeight: 510, letterSpacing: '-0.7px', lineHeight: 1.05 }

const cardClass = 'relative rounded-lg bg-[var(--ed-bg-idle)] border border-[var(--ed-border)]'
const primaryBtn =
  'inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[var(--ed-brand)] hover:bg-[var(--ed-accent-hover)] active:bg-[var(--ed-accent-pressed)] transition-colors duration-200 text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[var(--ed-brand)]'
const secondaryBtn =
  'inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[var(--ed-bg-idle)] border border-[var(--ed-border)] text-[var(--ed-text-2)] hover:bg-[var(--ed-bg-hover)] hover:border-[var(--ed-border-strong)] transition-colors'
const btnText: React.CSSProperties = { fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }

const STEP_ICONS: Record<string, PhosphorIconType> = {
  add_contacts: UsersThree,
  connect_inbox: EnvelopeSimple,
  activate_workflow: TreeStructure,
  launch_campaign: Rocket,
  setup_warmup: Lightning,
  domain_auth: ShieldCheck,
  enrich_contacts: Sparkle,
  add_materials: FileText,
  use_templates: Browsers,
  connect_calendar: CalendarBlank,
  connect_crm: PlugsConnected,
}

/** KpiCard-style icon chip: h-9 w-9 rounded-md, nested bg, accent icon. */
const IconChip: React.FC<{ icon: PhosphorIconType }> = ({ icon: Icon }) => (
  <div className="h-9 w-9 shrink-0 rounded-md bg-[var(--ed-bg-nested)] border border-[var(--ed-border-subtle)] flex items-center justify-center">
    <Icon className="h-[18px] w-[18px] text-[var(--ed-accent)]" weight="regular" aria-hidden="true" />
  </div>
)

const ProgressBar: React.FC<{ percent: number; label?: string }> = ({ percent, label }) => (
  <div
    role="progressbar"
    aria-valuemin={0}
    aria-valuemax={100}
    aria-valuenow={percent}
    aria-label={label ?? 'Setup progress'}
    className="h-1.5 w-full rounded-full bg-[var(--ed-bg-track)] overflow-hidden"
  >
    <motion.div
      className="h-full rounded-full bg-[var(--ed-brand)]"
      initial={{ width: 0 }}
      animate={{ width: `${percent}%` }}
      transition={{ duration: 0.8, ease: easeOutExpo }}
    />
  </div>
)

const StepIcon: React.FC<{ done: boolean }> = ({ done }) =>
  done ? (
    <CheckCircle className="h-5 w-5 shrink-0 text-[var(--ed-success)]" weight="fill" aria-label="Done" />
  ) : (
    <Circle className="h-5 w-5 shrink-0 text-[var(--ed-text-5)]" weight="regular" aria-label="Not done" />
  )

// ---------- Next-best-action hero (D4) ----------

export const NextStepHero: React.FC<{
  status: OnboardingStatus
  firstName: string
  onGo: (route: string) => void
  onLaunch: () => void
}> = ({ status, firstName, onGo, onLaunch }) => {
  const next = status.next_step
  const launch = isLaunchHero(status)
  const requiredLeft = status.tiers.required.total - status.tiers.required.done

  if (!next) return null

  const stepNumber = status.tiers.required.done + 1
  const eyebrow = launch
    ? 'Ready to launch'
    : requiredLeft > 0
      ? `Required · step ${Math.min(stepNumber, status.tiers.required.total)} of ${status.tiers.required.total}`
      : 'Recommended next'

  return (
    <section className={`${cardClass} p-6`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <IconChip icon={STEP_ICONS[next.key] ?? Rocket} />
          <div className="min-w-0">
            <p className="text-[var(--ed-text-4)]" style={{ ...monoFont, fontSize: '10px' }}>
              {eyebrow}
            </p>
            <h2
              className="text-[var(--ed-text-1)] mt-2"
              style={{ fontSize: '24px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.288px' }}
            >
              {launch ? `Launch your first campaign, ${firstName}` : next.label}
            </h2>
            <p
              className="text-[var(--ed-text-3)] mt-1.5 max-w-xl"
              style={{ fontSize: '15px', lineHeight: 1.6, letterSpacing: '-0.165px' }}
            >
              {launch
                ? 'Your contacts, inbox and workflow are ready. Send your first emails whenever you are.'
                : next.description}
            </p>

            {launch && (
              <p
                className="mt-4 inline-flex items-start gap-2 rounded-md px-3 py-2 bg-[var(--ed-warn-soft-1)] border border-[var(--ed-warn-border)] text-[var(--ed-warn-text)]"
                style={{ fontSize: '12px', lineHeight: 1.5 }}
              >
                <Lightbulb className="h-4 w-4 mt-px shrink-0 text-[var(--ed-warn-icon)]" weight="fill" />
                {WARMUP_TIP}
              </p>
            )}

            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <motion.button
                onClick={() => (launch ? onLaunch() : onGo(next.route))}
                className={`group ${primaryBtn}`}
                style={btnText}
                whileTap={{ scale: 0.97 }}
              >
                {launch ? <Rocket className="h-4 w-4" weight="fill" /> : null}
                {launch ? 'Launch campaign' : next.label}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" weight="bold" />
              </motion.button>
              {launch && (
                <motion.button
                  // Settings > Mailboxes, the same place the checklist's
                  // "Connect a sending inbox" step now sends people.
                  onClick={() => onGo('/settings?section=email-accounts')}
                  className={secondaryBtn}
                  style={btnText}
                  whileTap={{ scale: 0.98 }}
                >
                  Set up warm-up first
                </motion.button>
              )}
            </div>
          </div>
        </div>

        <div className="lg:w-56 shrink-0">
          <div className="flex items-baseline justify-between mb-2.5">
            <span className="text-[var(--ed-text-4)]" style={{ ...monoFont, fontSize: '10px' }}>
              Setup
            </span>
            <span className="text-[var(--ed-text-1)] tabular-nums" style={bigNumber}>
              {status.percent}%
            </span>
          </div>
          <ProgressBar percent={status.percent} />
          <p className="text-[var(--ed-text-4)] mt-2 tabular-nums" style={{ fontSize: '12px', letterSpacing: '-0.13px' }}>
            {status.completed} of {status.total} steps done
          </p>
        </div>
      </div>
    </section>
  )
}

// ---------- Setup checklist (D2) ----------

const StepRow: React.FC<{ step: OnboardingStep; onGo: (route: string) => void }> = ({ step, onGo }) => (
  <button
    onClick={() => !step.done && onGo(step.route)}
    disabled={step.done}
    className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left transition-colors ${
      step.done ? 'cursor-default' : 'hover:bg-[var(--ed-bg-hover)]'
    }`}
  >
    <StepIcon done={step.done} />
    <span className="flex-1 min-w-0">
      <span
        className={step.done ? 'text-[var(--ed-text-4)] line-through' : 'text-[var(--ed-text-1)]'}
        style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
      >
        {step.label}
      </span>
      {!step.done && (
        <span className="block text-[var(--ed-text-4)] truncate" style={{ fontSize: '12px', letterSpacing: '-0.13px' }}>
          {step.description}
        </span>
      )}
    </span>
    {!step.done && (
      <ArrowRight
        className="h-3.5 w-3.5 text-[var(--ed-text-4)] opacity-0 group-hover:opacity-100 transition-opacity"
        weight="bold"
      />
    )}
  </button>
)

const CollapsibleTier: React.FC<{
  title: string
  summary: string
  steps: OnboardingStep[]
  onGo: (route: string) => void
}> = ({ title, summary, steps, onGo }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-t border-[var(--ed-border-subtle)] pt-1">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-3 rounded-md hover:bg-[var(--ed-bg-hover)] transition-colors"
      >
        <span className="text-[var(--ed-text-2)]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
          {title}
        </span>
        <span className="flex items-center gap-2 text-[var(--ed-text-4)] tabular-nums" style={{ fontSize: '12px' }}>
          {summary}
          <CaretDown
            className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            weight="bold"
          />
        </span>
      </button>
      {open && (
        <div className="pb-1">
          {steps.map((s) => (
            <StepRow key={s.key} step={s} onGo={onGo} />
          ))}
        </div>
      )}
    </div>
  )
}

export const SetupChecklist: React.FC<{
  status: OnboardingStatus
  onGo: (route: string) => void
  onLaunch: () => void
}> = ({ status, onGo, onLaunch }) => {
  const tier = (t: StepTier) => ({ steps: stepsForTier(status, t), summary: tierSummary(status, t) })
  const rec = tier('recommended')
  const opt = tier('optional')

  return (
    <section className={`${cardClass} p-5`}>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="text-[var(--ed-text-1)]" style={cardTitle}>
            Set up your account
          </h2>
          <p className="text-[var(--ed-text-3)] mt-0.5" style={cardDesc}>
            Finish the three required steps to launch. The rest improves your results.
          </p>
        </div>
        <span className="text-[var(--ed-text-1)] tabular-nums" style={bigNumber}>
          {status.percent}%
        </span>
      </div>
      <ProgressBar percent={status.percent} />

      <div className="mt-5">
        <div className="flex items-center justify-between px-3 mb-1">
          <span className="text-[var(--ed-text-4)]" style={{ ...monoFont, fontSize: '10px' }}>
            Required to launch
          </span>
          <span className="text-[var(--ed-text-4)] tabular-nums" style={{ fontSize: '12px' }}>
            {tierSummary(status, 'required')}
          </span>
        </div>
        {stepsForTier(status, 'required').map((s) => (
          <StepRow key={s.key} step={s} onGo={onGo} />
        ))}
      </div>

      <div className="mt-3">
        <CollapsibleTier title="Recommended for best results" summary={rec.summary} steps={rec.steps} onGo={onGo} />
        <CollapsibleTier title="Optional enhancements" summary={opt.summary} steps={opt.steps} onGo={onGo} />
      </div>

      <div className="mt-4 pt-4 border-t border-[var(--ed-border-subtle)] flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[var(--ed-text-4)]" style={{ fontSize: '12px', letterSpacing: '-0.13px' }}>
          {status.can_launch
            ? 'Required steps complete. You can launch any time.'
            : 'Launch unlocks when the required steps are done.'}
        </p>
        <motion.button
          onClick={onLaunch}
          disabled={!status.can_launch}
          className={primaryBtn}
          style={btnText}
          whileTap={status.can_launch ? { scale: 0.97 } : undefined}
        >
          <Rocket className="h-4 w-4" weight="fill" />
          Launch campaign
        </motion.button>
      </div>
    </section>
  )
}

// ---------- Support strip (D5) ----------

export const SupportStrip: React.FC = () => (
  <section className={`${cardClass} p-5 flex items-center justify-between gap-4 flex-wrap`}>
    <div className="flex items-center gap-4 min-w-0">
      <IconChip icon={BookOpenText} />
      <div className="min-w-0">
        <p className="text-[var(--ed-text-1)]" style={cardTitle}>
          New to Beevelope, or facing any challenges?
        </p>
        <p className="text-[var(--ed-text-3)] mt-0.5" style={cardDesc}>
          Write to us and a real person will help you out.
        </p>
      </div>
    </div>
    <a href={SUPPORT_MAILTO} className={secondaryBtn} style={btnText}>
      <EnvelopeSimple className="h-4 w-4 text-[var(--ed-text-3)]" weight="regular" />
      Email {SUPPORT_EMAIL}
    </a>
  </section>
)

// ---------- Profile completion (D6) ----------

export const ProfileCompletionCard: React.FC<{
  status: OnboardingStatus
  mode: 'progress' | 'complete'
  onGo: (route: string) => void
  onDismiss: () => void
}> = ({ status, mode, onGo, onDismiss }) => {
  if (mode === 'complete') {
    return (
      <section className={`${cardClass} px-5 py-4 flex items-center gap-3`}>
        <CheckCircle className="h-5 w-5 text-[var(--ed-success)]" weight="fill" />
        <p className="text-[var(--ed-text-2)]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
          Setup complete. Everything is connected.
        </p>
      </section>
    )
  }

  const left = remainingSteps(status).slice(0, 4)
  return (
    <section className={`${cardClass} p-5`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[var(--ed-text-4)] mb-1.5" style={{ ...monoFont, fontSize: '10px' }}>
            Profile completion
          </p>
          <p className="text-[var(--ed-text-1)] tabular-nums" style={cardTitle}>
            {status.percent}% set up
            <span className="text-[var(--ed-text-4)] font-normal">
              {' '}
              · {status.total - status.completed} steps left
            </span>
          </p>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss profile completion"
          className="h-7 w-7 flex items-center justify-center rounded-md text-[var(--ed-text-4)] hover:text-[var(--ed-text-2)] hover:bg-[var(--ed-bg-hover)] transition-colors"
        >
          <X className="h-4 w-4" weight="bold" />
        </button>
      </div>
      <div className="mt-3">
        <ProgressBar percent={status.percent} label="Profile completion" />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {left.map((s) => (
          <button
            key={s.key}
            onClick={() => onGo(s.route)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[var(--ed-bg-idle)] border border-[var(--ed-border)] text-[var(--ed-text-2)] hover:bg-[var(--ed-bg-hover)] hover:border-[var(--ed-accent-border)] transition-colors"
            style={{ fontSize: '12px', fontWeight: 510 }}
          >
            {s.label}
            <ArrowRight className="h-3 w-3 text-[var(--ed-text-4)]" weight="bold" />
          </button>
        ))}
      </div>
    </section>
  )
}
